import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '@/shared/lib/api/errors';
import { formatRelativeBr } from '@/shared/lib/utils/dateTimeBr';
import { useToast } from '@/shared/hooks/useToast';
import { Spinner } from '@/shared/ui';
import { estiloTipoNotificacao } from './estiloTipoNotificacao';
import { destinoNotificacao } from './destinoNotificacao';
import type { Notificacao } from './notificacaoTypes';
import {
  useMarcarNotificacaoLida,
  useMarcarTodasNotificacoesLidas,
  useNotificacoesLista,
} from './useNotificacaoQueries';
import styles from './NotificacoesPopover.module.css';

type NotificacoesPopoverProps = {
  aberto: boolean;
  temNaoLidas: boolean;
  onFechar: () => void;
};

export function NotificacoesPopover({
  aberto,
  temNaoLidas,
  onFechar,
}: NotificacoesPopoverProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const lista = useNotificacoesLista(aberto);
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodas = useMarcarTodasNotificacoesLidas();

  const itens = lista.data?.itens ?? [];

  const abrir = (n: Notificacao) => {
    if (!n.lida) {
      marcarLida.mutate(n.id);
    }
    onFechar();
    const destino = destinoNotificacao(n);
    if (destino) {
      navigate(destino);
    }
  };

  const marcarTodasLidas = async () => {
    try {
      await marcarTodas.mutateAsync();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className={styles.popover} role="dialog" aria-label="Notificacoes">
      <div className={styles.head}>
        <p className={styles.title}>Notificações</p>
        {temNaoLidas ? (
          <button
            type="button"
            className={styles.marcarTodas}
            onClick={() => void marcarTodasLidas()}
            disabled={marcarTodas.isPending}
          >
            Marcar todas como lidas
          </button>
        ) : null}
      </div>

      {lista.isLoading ? (
        <div className={styles.centro}>
          <Spinner />
        </div>
      ) : null}

      {lista.isError ? (
        <p className={styles.erro}>{getErrorMessage(lista.error)}</p>
      ) : null}

      {!lista.isLoading && !lista.isError && itens.length === 0 ? (
        <p className={styles.vazio}>Nenhuma notificacao no momento.</p>
      ) : null}

      {itens.length > 0 ? (
        <ul className={styles.lista}>
          {itens.map((n) => {
            const estilo = estiloTipoNotificacao(n.tipo);
            return (
              <li key={n.id}>
                <button
                  type="button"
                  className={`${styles.item} ${n.lida ? styles.itemLido : ''}`}
                  style={{ borderLeftColor: estilo.corVar }}
                  onClick={() => abrir(n)}
                >
                  <span className={styles.itemTopo}>
                    <span className={styles.kicker} style={{ color: estilo.corVar }}>
                      {estilo.rotulo}
                    </span>
                    {!n.lida ? <span className={styles.dot} aria-label="Nao lida" /> : null}
                  </span>
                  {n.titulo ? <span className={styles.itemTitulo}>{n.titulo}</span> : null}
                  {n.mensagem ? <span className={styles.itemTexto}>{n.mensagem}</span> : null}
                  {n.criadoEm ? (
                    <span className={styles.itemData}>{formatRelativeBr(n.criadoEm)}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
